import { useEffect, useState } from "react";
import { createProgramMediaComment, getProgramMediaComments, getProgramMediaSingle, getProgramSingle } from "../../../../services/program";
import { emptyProgram, emptyProgramMedia, Program, ProgramMedia, ProgramMediaComment } from "../../../../interfaces/program";
import { Alert, Button, Form } from "react-bootstrap";
import { t } from "i18next";
import Uploader from "../../../general/uploader";
import { Media } from "../../../../interfaces/media";
import moment from 'moment';
import { LoadingSpinner } from "../../../general/loading-spinner";

interface ProgramMediaCommentsParams {
    program_id: number;
    media_id: number;
    onClose?: CallableFunction;
}

export function ProgramMediaComments(props: ProgramMediaCommentsParams) {
    const [program, setProgram] = useState<Program>(emptyProgram);
    const [programMedia, setProgramMedia] = useState<ProgramMedia>(emptyProgramMedia);
    const [programMediaComments, setProgramMediaComments] = useState<ProgramMediaComment[]>([]);
    const [showCommentForm, setShowCommentForm] = useState(false);
    const [media, setMedia] = useState<Media[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [lightboxImages, setLightboxImages] = useState<{ url: string, name: string }[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    useEffect(() => {
        Promise.all([
            getProgramSingle(props.program_id),
            getProgramMediaSingle(props.media_id),
            getProgramMediaComments(props.media_id)
        ]).then(response => {
            setProgram(response[0].data);
            setProgramMedia(response[1].data);
            setProgramMediaComments(response[2].data);
            setLoaded(true);
        })
        getProgramMediaComments(props.media_id)
            .then(response => {
                setProgramMediaComments(response.data);
                
            });
    }, []);


    const handleShowCommentForm = () => {
        setShowCommentForm(true);
    }

    const handleClose = () => {
        setShowCommentForm(false);
        setMedia([]); // Limpiar el archivo adjunto al cerrar
    }

    const handleNewComment = (event: any) => {
        event.preventDefault();

        const body = {
            media_id: props.media_id,
            comment: event.target.comment.value,
            media: media.map(m => m.id) // Enviar array de IDs de media
        }
        createProgramMediaComment(body)
            .then(response => {
                if (response.data.status == 1) {
                    const newProgramMediaComments = [
                        response.data.data,
                        ...programMediaComments
                    ];
                    setProgramMediaComments(newProgramMediaComments);
                    setShowCommentForm(false);
                    setMedia([]); // Limpiar los archivos adjuntos
                } else {
                    alert(response.data.message);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleMediaEventUpload = (media: Media[]) => {
        const newMedia = media.map((m: Media) => { return m; });
        setMedia(newMedia);
    }

    // Lightbox functions
    const openLightbox = (images: Media[], index: number) => {
        const imageList = images.map(img => ({ url: img.url, name: img.name }));
        setLightboxImages(imageList);
        setCurrentImageIndex(index);
    }

    const closeLightbox = () => {
        setLightboxImages([]);
        setCurrentImageIndex(0);
    }

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % lightboxImages.length);
    }

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
    }

    // Helper function to get file icon
    const getFileIcon = (media: Media): string => {
        const mimetype = media.mimetype;
        switch (mimetype) {
            case 'application/pdf':
                return 'bi-file-earmark-pdf';
            case 'application/vnd.ms-powerpoint':
            case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            case 'application/vnd.apple.keynote':
                return 'bi-file-earmark-ppt';
            case 'application/msword':
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            case 'application/vnd.apple.pages':
                return 'bi-file-earmark-word';
            case 'application/vnd.ms-excel':
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            case 'application/vnd.apple.numbers':
                return 'bi-file-earmark-excel';
            case 'image/png':
            case 'image/jpeg':
                return 'bi-file-image';
            case 'video/mp4':
                return 'bi-file-earmark-play';
            case 'audio/*':
                return 'bi-file-earmark-music';
            case 'application/zip':
            case 'application/x-rar':
                return 'bi-file-earmark-zip';
            default:
                return 'bi-file-earmark-text';
        }
    }

    return (
        <div className="program-media-comments-container">
            { !loaded && <LoadingSpinner /> }
            { loaded &&
                <div className="comment-actions">
                    <div className="comments-header">
                        <h2>{ t("Comments") }</h2>
                        <div className="comments-actions-buttons">
                            <Button onClick={ handleShowCommentForm }>
                                { t("Add comment") }
                            </Button>
                            { props.onClose &&
                                <Button
                                    variant="outline-secondary"
                                    onClick={ (e) => props.onClose!(e) }
                                    title={t("Close")}
                                >
                                    <i className="bi bi-x-lg"></i> {t("Close")}
                                </Button>
                            }
                        </div>
                    </div>
                    { showCommentForm &&
                        <div className="comment-form-container">
                            <Form onSubmit={ handleNewComment }>
                                <div className="fields">
                                    <Form.Group className="mb-3" controlId="media_id">
                                        <Form.Control type="hidden" name="media_id" required value={ props.media_id } />
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="comment">
                                        <Form.Label aria-required={ true }>{ t("Comment") }</Form.Label>
                                        <Form.Control name="comment" as="textarea" required />
                                    </Form.Group>
                                    <Form.Group className="mb-3 media-field" controlId="media">
                                        <Uploader title={ t("Upload video, file or document") }
                                            media={ media } save={ handleMediaEventUpload } />
                                    </Form.Group>
                                </div>
                                <div className="actions">
                                    <Button type="submit" variant="primary">
                                        { t("Save") }
                                    </Button>
                                    <Button variant="secondary" onClick={ handleClose }>
                                        { t("Close") }
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    }
                </div>
            }
            { loaded && programMediaComments.length == 0 &&
                <Alert key={'info'} variant={'info'}>
                    { t("This media has no coments.") }
                </Alert>
            }
            { loaded && programMediaComments.length > 0 &&
                <div className="coments-container">
                    { programMediaComments.map((c: ProgramMediaComment) => {
                        return <div className="comment-item" key={ 'comment-' + c.id }>
                                <div className="meta">
                                    { c.user_display_name } - { moment(c.created_at).format('DD/MM/YYYY h:m') }
                                </div>
                                <div className="comment-content">
                                    <i className="bi bi-quote"></i> { c.comment }
                                </div>
                                { c.media.length > 0 && (() => {
                                    const images = c.media.filter(m => ['image/png', 'image/jpeg'].includes(m.mimetype));
                                    const videos = c.media.filter(m => ['video/mp4'].includes(m.mimetype));
                                    const files = c.media.filter(m => !['image/png', 'image/jpeg', 'video/mp4'].includes(m.mimetype));

                                    return (
                                        <div className="comment-media">
                                            {/* Cuadrícula de imágenes */}
                                            {images.length > 0 && (
                                                <div className="comment-images-grid">
                                                    {images.map((img, index) => (
                                                        <div key={`img-${c.id}-${index}`} className="comment-image-grid-item">
                                                            <img src={ img.url } alt={ img.name } />
                                                            <div className="image-overlay">
                                                                <button
                                                                    onClick={() => openLightbox(images, index)}
                                                                    className="image-action"
                                                                    title={ t("View full size") }
                                                                >
                                                                    <i className="bi bi-zoom-in"></i>
                                                                </button>
                                                                <a
                                                                    href={ img.url }
                                                                    download={ img.name }
                                                                    className="image-action"
                                                                    title={ t("Download") }
                                                                >
                                                                    <i className="bi bi-download"></i>
                                                                </a>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Videos */}
                                            {videos.length > 0 && (
                                                <div className="comment-videos-list">
                                                    {videos.map((vid, index) => (
                                                        <div key={`vid-${c.id}-${index}`} className="comment-video">
                                                            <video width="100%" controls>
                                                                <source src={ vid.url } type="video/mp4" />
                                                            </video>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Otros archivos */}
                                            {files.length > 0 && (
                                                <div className="comment-files-list">
                                                    {files.map((file, index) => (
                                                        <a
                                                            key={`file-${c.id}-${index}`}
                                                            href={ file.url }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="comment-file-download"
                                                            title={ file.name }
                                                        >
                                                            <i className={`bi ${getFileIcon(file)}`}></i>
                                                            <span className="file-name">{ file.name }</span>
                                                            <i className="bi bi-arrow-up-right-square download-icon"></i>
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                    })}
                </div>
            }
            {lightboxImages.length > 0 && (
                <div className="image-lightbox" onClick={closeLightbox}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={closeLightbox} title={t("Close")}>
                            <i className="bi bi-x-lg"></i>
                        </button>

                        {lightboxImages.length > 1 && (
                            <>
                                <button className="lightbox-nav lightbox-prev" onClick={prevImage} title={t("Previous")}>
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <button className="lightbox-nav lightbox-next" onClick={nextImage} title={t("Next")}>
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </>
                        )}

                        <img src={lightboxImages[currentImageIndex].url} alt={lightboxImages[currentImageIndex].name} />

                        {lightboxImages.length > 1 && (
                            <div className="lightbox-counter">
                                {currentImageIndex + 1} / {lightboxImages.length}
                            </div>
                        )}

                        <div className="lightbox-actions">
                            <a
                                href={lightboxImages[currentImageIndex].url}
                                download={lightboxImages[currentImageIndex].name}
                                className="lightbox-download"
                                title={t("Download")}
                            >
                                <i className="bi bi-download"></i> {t("Download")}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}